import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  objectivesApi,
  teachurKeys,
  useGetCourseLessonsQuery,
  type Lesson,
  type Objective,
} from '@teachur/http-client';
import { computeCourseStats, type CourseStats } from '../lib/courseStats';

interface UseCourseStatsResult {
  stats: CourseStats | null;
  isLoading: boolean;
}

/**
 * Aggregates course statistics for the instructor "Course Data" panel.
 *
 * The lightweight lesson-objective list endpoint does not include media or
 * assessments, so we collect every objective id across the course lessons and
 * fetch each objective's full detail (which carries `media` and `assessments`).
 * React Query caches these per objective, so they are shared with the
 * Objectives tab and are not re-fetched unnecessarily.
 */
export const useCourseStats = (courseId: number): UseCourseStatsResult => {
  const { data: lessons, isLoading: isLessonsLoading } = useGetCourseLessonsQuery(courseId);

  // Collect unique objective ids across all lessons.
  const objectiveIds = useMemo(() => {
    const ids = new Set<number>();
    (lessons ?? []).forEach((lesson: Lesson) => {
      (lesson.objectiveIds ?? []).forEach((id) => ids.add(id));
      (lesson.objectives ?? []).forEach((lo) => {
        if (lo.objectiveId) ids.add(lo.objectiveId);
      });
    });
    return Array.from(ids);
  }, [lessons]);

  const objectiveQueries = useQueries({
    queries: objectiveIds.map((id) => ({
      queryKey: teachurKeys.objectives.detail(id),
      queryFn: async () => {
        const response = await objectivesApi.getOne(id);
        return response.data;
      },
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
    })),
  });

  const isObjectivesLoading = objectiveQueries.some((q) => q.isLoading);

  const objectives = useMemo(
    () => objectiveQueries.map((q) => q.data).filter((o): o is Objective => Boolean(o)),
    [objectiveQueries]
  );

  const stats = useMemo(() => {
    if (isLessonsLoading || isObjectivesLoading) {
      return null;
    }
    return computeCourseStats(lessons, objectives);
  }, [lessons, objectives, isLessonsLoading, isObjectivesLoading]);

  return {
    stats,
    isLoading: isLessonsLoading || isObjectivesLoading,
  };
};
